import {
  createFailAction,
  createStartAction,
  createSuccessAction,
} from '../redux-util/async-helpers';
import { getQueryValue } from '../../utils/route';
import merge from 'merge';
import { localizeModules } from '../localize-modules';
import { getGradeLevel, isTeacher } from '../redux-bridge/session/selectors';
import { makeActionCreator } from '../redux-util/general-helpers';
import { noticeError } from 'app/api/analytics/reporting';
import { importAll } from '../../utils/async';
import { getBrand, hasBrandError } from './watson-config-selectors';
import { getBrandCommon, isJigsawOnly } from './watson-config-selectors';
import {
  isLoggedIn,
  isSessionPending,
} from '../redux-bridgesaw/bridgesaw-selectors';
import { isKiddieUser } from '../redux-bridge/users/utils';
import { isEmpty } from '../../utils/objects';
import {
  clearUiBaseCookie,
  getUiBaseCookie,
  setUiBaseCookie,
} from '../cookies';
import chameleonjs from 'chameleonjs';
import { loadTheme } from 'app/api/redux-watson/theme-actions';

export const LOCALE_KEY = 'config.locale';
export const THEME_KEY = 'config.theme';
export const BRAND_KEY = 'config.brand';
export const UIBASE_KEY = 'config.uiBase';

const DEFAULT_LOCALE = 'en';

const UI_BASES = ['kiddie', 'middle'];
const UI_BASE_QUERY_PARAM = 'uiBase';

const setUiBase = makeActionCreator(UIBASE_KEY, 'data');

function getConfigBases(state) {
  // Due to the way the dependencies work, config data loads twice:
  // Once to get the base config only prior to loading the session, and again after
  // the session has loaded to complete the config. uiBase and logged-in
  // determination depend on the base config, so don't even try to load additional
  // data during the first load phase, since it leads to js errors and such.
  if (!isSessionPending(state) && isLoggedIn(state)) {
    const bases = [getUiBase(state)];
    if (isTeacher(state)) {
      bases.push('teacher');
    }
    return bases.filter(b => !!b);
  }
  return [];
}

async function importForAll(state, importer, base) {
  const bases = getConfigBases(state);
  if (base) {
    bases.push(base);
  }
  const importers = bases.map(b => importer(b));
  const result = await importAll(importers);
  return result.filter(i => !!i);
}

function getUiBase(state) {
  if (!isLoggedIn(state)) {
    // Don't load level-specific content if we don't need it
    return null;
  }

  // Teacher login and internal testing
  // Use cookie to maintain setting across reloads
  const queryVal = getQueryValue(UI_BASE_QUERY_PARAM);
  const cookieVal = getUiBaseCookie();
  const grade = getGradeLevel(state);

  if (queryVal) {
    if (UI_BASES.indexOf(queryVal) >= 0) {
      return queryVal;
    }
    return 'middle';
  } else if (isJigsawOnly(state)) {
    return getBrandCommon(state).jigsawOnly.reader;
  } else if (cookieVal && !grade) {
    if (UI_BASES.indexOf(cookieVal) >= 0) {
      return cookieVal;
    }
    return 'middle';
  }

  // Just base it off of session for students
  if (grade && isKiddieUser(grade)) {
    return 'kiddie';
  }
  return 'middle';
}

export function loadLocale(locale) {
  return async function(dispatch, getState) {
    locale = locale.split('_').join('-');
    dispatch(createStartAction(LOCALE_KEY, locale));
    const localeData = await importForAll(
      getState(),
      s => () => import(`../../../config/locales/${locale}/${s}.json`),
      'common'
    );
    let defaultData = [];
    if (locale !== DEFAULT_LOCALE) {
      defaultData = await importForAll(
        getState(),
        s => () =>
          import(`../../../config/locales/${DEFAULT_LOCALE}/${s}.json`),
        'common'
      );
    }

    const data = [...localeData, ...defaultData];

    const finalData = merge.recursive(...data.reverse());
    if (Object.keys(finalData).length > 0) {
      await localizeModules(locale);
      delete finalData.smartling;
      dispatch(createSuccessAction(LOCALE_KEY, finalData));
    } else {
      noticeError('Could not load any locale data for ' + locale);
      dispatch(createFailAction(LOCALE_KEY));
    }
  };
}

// This is only used to determine the initial brand at load time in
// order to initialize the store. After the brand has loaded, you
// should ask redux for the brand id. This is so we do not have
// to maintain a brand query value through url changes.
function getStartBrand() {
  const urlBrand = window.location.hostname;
  const queryBrand = getQueryValue('brand');
  return queryBrand || urlBrand;
}

function getStartLocale(brandLocale) {
  const queryLocale = getQueryValue('locale');
  return queryLocale || brandLocale || DEFAULT_LOCALE;
}

async function fetchBrand(brandId) {
  let localBrandData = {};
  let remoteBrandData = {};
  let brandToFetch = brandId;
  try {
    const brandModule = await import(`../../../config/brands/${brandId}.json`);
    localBrandData = brandModule.default || brandModule;
    brandToFetch = localBrandData.inherit;
    delete localBrandData.inherit;
  } catch (e) {
    // Ok
  }

  try {
    remoteBrandData = await chameleonjs.loadBrand(brandToFetch, 'watson');
  } catch (e) {
    localBrandData = {};
  }

  return merge.recursive(remoteBrandData, localBrandData);
}

export function loadBrand() {
  return async function(dispatch) {
    const brand = getStartBrand();
    dispatch(createStartAction(BRAND_KEY, brand));
    const data = await fetchBrand(brand);
    if (!isEmpty(data)) {
      dispatch(createSuccessAction(BRAND_KEY, data));
    } else {
      dispatch(createFailAction(BRAND_KEY));
      noticeError('Could not load any brand for ' + brand);
    }
  };
}

export function loadLocaleAndTheme() {
  return async function(dispatch, getState) {
    const uiBase = getUiBase(getState());
    dispatch(setUiBase(uiBase));
    // The query param may disappear as you navigate around. We need to persist
    // the teacher's selection for reloads
    if (uiBase) {
      setUiBaseCookie(uiBase);
    } else {
      clearUiBaseCookie();
    }

    const brand = getBrand(getState());

    if (!hasBrandError(getState())) {
      dispatch(loadLocale(getStartLocale(brand.common.locale)));
    } else {
      dispatch(loadLocale(getStartLocale()));
    }
    dispatch(loadTheme());
  };
}
